import apiClient from './api.service';
import type {
  AiArtifact,
  AiArtifactType,
  AiGenerationRequest,
  AiProviderOption,
} from '../types/ai-analysis.types';

const aiAnalysisService = {
  async getCatalog(artifactType?: AiArtifactType): Promise<AiProviderOption[]> {
    const response = await apiClient.get('/api/ai/providers', { params: { artifactType } });
    return response.data.data;
  },
  async createRequest(
    surveyId: string,
    body: {
      selections: Array<{ artifactType: AiArtifactType; modelId: string }>;
      externalContextEnabled: boolean;
      microdataProcessingAcknowledged: boolean;
    }
  ): Promise<AiGenerationRequest> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/ai-generation-requests`, body);
    return response.data;
  },
  async listRequests(surveyId: string): Promise<AiGenerationRequest[]> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/ai-generation-requests`);
    return response.data.map(normalizeRequest);
  },
  async getRequest(requestId: string): Promise<AiGenerationRequest> {
    const response = await apiClient.get(`/api/ai-generation-requests/${requestId}`);
    return response.data;
  },
  async cancelRequest(requestId: string) {
    return (await apiClient.post(`/api/ai-generation-requests/${requestId}/cancellations`)).data;
  },
  async getArtifact(artifactId: string): Promise<AiArtifact> {
    return (await apiClient.get(`/api/ai-artifacts/${artifactId}`)).data;
  },
  async createRevision(artifactId: string, sourceText: string, normalizedPayload: unknown) {
    return (
      await apiClient.post(`/api/ai-artifacts/${artifactId}/revisions`, {
        sourceText,
        normalizedPayload,
      })
    ).data;
  },
  async decide(
    artifactId: string,
    decisionType: 'APPROVE' | 'REJECT' | 'APPLY',
    options: { revisionId?: string; reason?: string; confirmStale?: boolean } = {}
  ) {
    return (
      await apiClient.post(`/api/ai-artifacts/${artifactId}/decisions`, {
        decisionType,
        ...options,
      })
    ).data;
  },
  async getAdminCatalog() {
    return (await apiClient.get('/api/admin/ai/providers')).data;
  },
  async patchProvider(providerId: string, data: Record<string, unknown>) {
    return (await apiClient.patch(`/api/admin/ai/providers/${providerId}`, data)).data;
  },
  async patchModel(modelId: string, data: Record<string, unknown>) {
    return (await apiClient.patch(`/api/admin/ai/models/${modelId}`, data)).data;
  },
  async checkProvider(providerId: string) {
    return (await apiClient.post(`/api/admin/ai/providers/${providerId}/health`)).data;
  },
};

const normalizeRequest = (request: any): AiGenerationRequest => ({
  ...request,
  jobs: (request.jobs ?? []).map((job: any) => ({
    ...job,
    providerCode: job.providerCode ?? job.providerCodeSnapshot,
    modelId: job.modelId,
    modelName: job.modelName ?? job.externalModelIdSnapshot,
    artifactId: job.artifactId ?? job.artifact?.id ?? null,
  })),
});

export default aiAnalysisService;
